import { HStack, PageContainer, PageHeader, Spread, Text } from '@/components/Atoms'
import ActiveBadge from '@/components/Badge/PresetBadges/ActiveBadge'
import { Block } from '@/components/Block'
import { Card, CardContent } from '@/components/Card'
import { DataTable, DataTableRow } from '@/components/DataTable'
import { BudgetAllocation } from '@/components/DataTable/CellContent/BudgetAllocation'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { PendingChanges } from '@/components/PendingChanges'
import { YearSwitch } from '@/components/YearSwitch'
import {
  type CrtVersionField,
  useCrtVersionSelectionSync,
} from '@/hooks/crt/useCrtVersionSelectionSync'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  useCopyPreviousBudgetEstimates,
  useUpsertBudgetEstimates,
} from '@/hooks/queries/crt/useBudgetEstimates'
import {
  BudgetTrackerSearch,
  budgetTrackerExportQueryOptions,
  budgetTrackerSearchSchema,
  perMonthLeftToSpend,
  useBudgetTracker,
} from '@/hooks/queries/crt/useBudgetTracker'
import { useIntlLocale } from '@/hooks/useIntlLocale'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { DATATABLE_NAMES } from '@/stores/datatable'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import {
  BUDGET_CATEGORIES,
  BudgetCategory,
  INDETERMINATE,
  MonthKey,
  YEAR_FLAGS,
  yearFlagToBudgetEstimateScope,
} from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InputCurrency,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  ToggleGroup,
  ToggleGroupItem,
  TooltipTrigger,
} from '@repo/ui/components'
import { Link, createFileRoute } from '@tanstack/react-router'
import { CellContext, ColumnDef, ColumnSort } from '@tanstack/react-table'
import {
  CopyIcon,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  FoldHorizontalIcon,
  PencilIcon,
  UnfoldHorizontalIcon,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AssumptionKind,
  type AssumptionSelection,
  toAssumptionBadges,
  toSelectionSearch,
} from '../../-components/assumptions/assumption-selection'
import { ConfigureAssumptions } from '../../-components/assumptions/ConfigureAssumptions'
import { CompactCurrencyCell } from '../-components/CompactCurrencyCell'
import { EditLeftToSpendMonthsDialog } from './-components/EditLeftToSpendMonthsDialog'

// Manual investments net against the budget buckets; exactly the selected programs count.
const ASSUMPTION_KINDS: AssumptionKind[] = ['manual']
const VERSION_FIELDS: CrtVersionField[] = ['manualInvestmentIds']

// The tracker's two views: the general budget (FMC / Vapour / NRT) and the key accounts (K/E buckets).
const BUDGET_VIEWS = { GENERAL: 'general', KEY_ACCOUNTS: 'keyAccounts' } as const

// Columns the accounts view leaves out: the bucket estimate and the leftover derived from it.
const ESTIMATE_COLUMN_IDS = new Set(['estimate', 'leftToSpend', 'monthlyLeftToSpend'])

// The categories the general view can narrow to — every category but key accounts, which is its own view.
const GENERAL_BUDGET_CATEGORIES = BUDGET_CATEGORIES.filter(
  (category) => category !== 'KEY_ACCOUNTS',
)

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/budget-tracker/')({
  component: RouteComponent,
  validateSearch: (search) => budgetTrackerSearchSchema.parse(search),
})

type DisplayRow = {
  budgetAllocation: string
  description: string | null
  isActive: boolean
  estimate: number
  serverEstimate: number
  actuals: number
  salesDealsActuals: number
  ipAccruals: number
  calcRunAccruals: number
  forecast: number
  manuals: number
  budgetTotal: number
  leftToSpend: number | null
  // The bucket's saved month selection
  leftToSpendMonths: MonthKey[]
  // The bucket's saved month selection limited by the crtPeriod and year parameters
  leftToSpendActiveMonths: MonthKey[]
  monthlyLeftToSpend: number | null
} & DataTableRow

// The per-source actuals columns only exist while the "by source" view is on; the combined column
// replaces all three when it is off. Both share the same cell renderer and i18n key shape
// (`budgetTracker.table.<id>`), so the columns are built from these ids.
type ActualsColumnId = 'actuals' | 'salesDealsActuals' | 'ipAccruals' | 'calcRunAccruals'
const ACTUALS_COMBINED_COLUMN_IDS: ActualsColumnId[] = ['actuals']
const ACTUALS_SOURCE_COLUMN_IDS: ActualsColumnId[] = [
  'salesDealsActuals',
  'ipAccruals',
  'calcRunAccruals',
]

// Which actuals columns the table renders for a given toggle state.
const actualsColumnIdsFor = (isExpanded: boolean): ActualsColumnId[] =>
  isExpanded ? ACTUALS_SOURCE_COLUMN_IDS : ACTUALS_COMBINED_COLUMN_IDS

const isActualsColumnId = (id: string | undefined): boolean =>
  [...ACTUALS_COMBINED_COLUMN_IDS, ...ACTUALS_SOURCE_COLUMN_IDS].some((columnId) => columnId === id)

function sortDisplayRows(rows: DisplayRow[], sort: ColumnSort | undefined): DisplayRow[] {
  const factor = sort?.desc ? -1 : 1
  const compare = (a: DisplayRow, b: DisplayRow): number => {
    switch (sort?.id) {
      case 'description':
        return (a.description ?? '').localeCompare(b.description ?? '') * factor
      case 'estimate':
        return (a.estimate - b.estimate) * factor
      case 'forecast':
        return (a.forecast - b.forecast) * factor
      case 'actuals':
        return (a.actuals - b.actuals) * factor
      case 'salesDealsActuals':
        return (a.salesDealsActuals - b.salesDealsActuals) * factor
      case 'ipAccruals':
        return (a.ipAccruals - b.ipAccruals) * factor
      case 'calcRunAccruals':
        return (a.calcRunAccruals - b.calcRunAccruals) * factor
      case 'manuals':
        return (a.manuals - b.manuals) * factor
      case 'budgetTotal':
        return (a.budgetTotal - b.budgetTotal) * factor
      // Rows without an estimate have no "left to spend"; keep them at the bottom either way. The two
      // columns need separate comparisons: each bucket spreads over its own months, so the monthly
      // figure no longer orders the same way as the total.
      case 'leftToSpend':
      case 'monthlyLeftToSpend': {
        const key = sort?.id === 'monthlyLeftToSpend' ? 'monthlyLeftToSpend' : 'leftToSpend'
        const left = a[key]
        const right = b[key]
        if (left === null && right === null) return 0
        if (left === null) return 1
        if (right === null) return -1
        return (left - right) * factor
      }
      case 'budgetAllocation':
        return a.budgetAllocation.localeCompare(b.budgetAllocation) * factor
      default:
        // Natural order: active rows first / inactive last (status descending), then by allocation.
        return (
          Number(b.isActive) - Number(a.isActive) ||
          a.budgetAllocation.localeCompare(b.budgetAllocation)
        )
    }
  }
  return [...rows].sort(compare)
}

function ExpandDetailsButton({
  isExpanded,
  onChange,
}: {
  isExpanded: boolean
  onChange: (isExpanded: boolean) => void
}) {
  const { t } = useTranslation('crt')
  const label = isExpanded
    ? t('budgetTracker.actions.showLessDetails')
    : t('budgetTracker.actions.showMoreDetails')
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label={label}
            onClick={() => onChange(!isExpanded)}
          >
            {isExpanded ? (
              <FoldHorizontalIcon className="size-4" />
            ) : (
              <UnfoldHorizontalIcon className="size-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Module-scoped so TanStack Table's `flexRender` keeps the same component identity across renders
// and React reconciles the input in place. Rebuilding this inline in the columns array would remount
// the input on every edit, dropping focus and swallowing clicks when moving between fields.
function EstimateCell({
  value,
  disabled,
  ariaLabel,
  onCommit,
}: {
  value: string
  disabled: boolean
  ariaLabel: string
  onCommit: (next: string) => void
}) {
  const locale = useIntlLocale()
  return (
    <HStack className="justify-end">
      <InputCurrency
        className="h-8 w-40"
        locale={locale}
        value={value}
        onCommit={onCommit}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    </HStack>
  )
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { permissions } = usePermissions()
  const canEditEstimates = hasPermissions(permissions, 'crt_budget_estimates_edit')
  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')
  // Inline estimate edits keyed by budget allocation code (one CRT period per page view).
  const [estimateEdits, setEstimateEdits] = useState<Record<string, string>>({})

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { crtPeriod: selectedCrtPeriodStr } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: VERSION_FIELDS,
  })
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configuration = { manualInvestmentIds: search.manualInvestmentIds }
  const configBadges = toAssumptionBadges(t, ASSUMPTION_KINDS, configuration)
  const handleApplyConfig = (selection: AssumptionSelection) => {
    navigate({ search: { ...search, ...toSelectionSearch(ASSUMPTION_KINDS, selection), page: 1 } })
  }
  const page = search.page ?? 1
  const isNextYear = search.year === YEAR_FLAGS.NY
  const isExpanded = search.isExpanded ?? false

  const { isPending, error, data } = useBudgetTracker({
    crtPeriod: selectedCrtPeriodStr,
    budgetCategory: search.budgetCategory,
    year: search.year,
    keyword: search.keyword,
    manualInvestmentIds: search.manualInvestmentIds,
  })

  const displayRows: DisplayRow[] = (data?.results ?? []).map((row) => {
    const editedValue = estimateEdits[row.budgetAllocation]
    const estimate =
      editedValue !== undefined ? Number(editedValue === '' ? '0' : editedValue) : row.estimate
    const budgetTotal = row.forecast + row.actuals + row.manuals
    const leftToSpend = estimate === 0 ? null : estimate - budgetTotal
    return {
      budgetAllocation: row.budgetAllocation,
      description: row.description,
      isActive: row.isActive,
      estimate,
      serverEstimate: row.estimate,
      actuals: row.actuals,
      salesDealsActuals: row.salesDealsActuals,
      ipAccruals: row.ipAccruals,
      calcRunAccruals: row.calcRunAccruals,
      forecast: row.forecast,
      manuals: row.manuals,
      budgetTotal,
      // Recomputed live from the (possibly edited) estimate so the row stays coherent before saving.
      // Without an estimate there is nothing to spend against, so leave it blank rather than negative.
      leftToSpend,
      leftToSpendMonths: row.leftToSpendMonths,
      leftToSpendActiveMonths: row.leftToSpendActiveMonths,
      // The backend resolved which months this bucket spreads over; dividing by their count here keeps
      // the cell in step with an unsaved estimate edit, through the same rule the server applies.
      monthlyLeftToSpend:
        leftToSpend === null
          ? null
          : perMonthLeftToSpend(leftToSpend, row.leftToSpendActiveMonths.length),
      rowStatus: editedValue !== undefined ? 'warning' : undefined,
    }
  })
  const sortedRows = sortDisplayRows(displayRows, search.sort)
  const pageRows = sortedRows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)

  // Column totals over every row, not just the page, so they follow unsaved estimate edits too.
  const sumColumn = (pick: (row: DisplayRow) => number) =>
    sortedRows.reduce((acc, row) => acc + pick(row), 0)
  const sumNullableColumn = (pick: (row: DisplayRow) => number | null) => {
    const known = sortedRows.map(pick).filter((v): v is number => v !== null)
    return known.length > 0 ? known.reduce((acc, v) => acc + v, 0) : null
  }
  const totals = {
    estimate: <CompactCurrencyCell value={sumColumn((row) => row.estimate)} isCad />,
    ...Object.fromEntries(
      actualsColumnIdsFor(isExpanded).map((id) => [
        id,
        <CompactCurrencyCell key={id} value={sumColumn((row) => row[id])} />,
      ]),
    ),
    forecast: <CompactCurrencyCell value={sumColumn((row) => row.forecast)} />,
    manuals: <CompactCurrencyCell value={sumColumn((row) => row.manuals)} />,
    budgetTotal: <CompactCurrencyCell value={sumColumn((row) => row.budgetTotal)} />,
    leftToSpend: (
      <CompactCurrencyCell isSigned value={sumNullableColumn((row) => row.leftToSpend)} />
    ),
    monthlyLeftToSpend: (
      <CompactCurrencyCell isSigned value={sumNullableColumn((row) => row.monthlyLeftToSpend)} />
    ),
  }

  const hasUnsavedChanges = Object.keys(estimateEdits).length > 0

  const { mutate: saveEstimates, isPending: isSavePending } = useUpsertBudgetEstimates({
    handleOnSuccess: () => setEstimateEdits({}),
  })

  // The bucket whose left-to-spend months are being edited; null when the dialog is closed. Held as a code
  // rather than a row so the dialog follows the live row — a saved mask is patched straight into the cache.
  const [monthsEditCode, setMonthsEditCode] = useState<string | null>(null)
  const monthsEditTarget =
    displayRows.find((row) => row.budgetAllocation === monthsEditCode) ?? null

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false)
  const { mutate: copyPreviousEstimates, isPending: isCopyPending } =
    useCopyPreviousBudgetEstimates({ handleOnSuccess: () => setEstimateEdits({}) })

  // A null/0 server estimate and an empty input are equivalent; clearing back to the server value
  // drops the edit so the row stops being flagged as unsaved.
  const handleCommitEstimate = (
    budgetAllocation: string,
    value: string,
    serverEstimate: number,
  ) => {
    const isPristine = Number(value === '' ? '0' : value) === serverEstimate
    setEstimateEdits((prev) => {
      if (isPristine) {
        const { [budgetAllocation]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [budgetAllocation]: value }
    })
  }

  // Read the latest edits and commit handler through refs so the cell renderer below stays
  // referentially stable across edits. If it closed over `estimateEdits` directly, every commit
  // would rebuild the columns and remount the inputs.
  const estimateEditsRef = useRef(estimateEdits)
  estimateEditsRef.current = estimateEdits
  const handleCommitEstimateRef = useRef(handleCommitEstimate)
  handleCommitEstimateRef.current = handleCommitEstimate

  // Stable cell function so tabbing/clicking between estimate inputs doesn't unmount and remount
  // the input (which would lose focus and swallow the click).
  const renderEstimateCell = useCallback(
    ({ row }: CellContext<DisplayRow, unknown>) => {
      const { budgetAllocation, serverEstimate } = row.original
      const value =
        estimateEditsRef.current[budgetAllocation] ??
        (serverEstimate === 0 ? '' : String(serverEstimate))

      return (
        <EstimateCell
          value={value}
          disabled={!canEditEstimates}
          ariaLabel={t('budgetTracker.table.estimate')}
          onCommit={(next) =>
            handleCommitEstimateRef.current(budgetAllocation, next, serverEstimate)
          }
        />
      )
    },
    [canEditEstimates, t],
  )

  const handleSaveEstimates = () => {
    const yearScope = yearFlagToBudgetEstimateScope(search.year ?? 'CY')
    saveEstimates({
      updates: Object.entries(estimateEdits).map(([budgetAllocation, value]) => ({
        budgetAllocation,
        crtPeriod: selectedCrtPeriodStr,
        yearScope,
        amount: Number(value === '' ? '0' : value),
      })),
    })
  }

  const handleConfirmCopyPrevious = () => {
    copyPreviousEstimates(selectedCrtPeriodStr)
    setIsCopyDialogOpen(false)
  }

  // Estimate edits belong to one year scope, so switching scope discards any pending edits.
  const handleYearChange = (nextIsNextYear: boolean) => {
    setEstimateEdits({})
    navigate({ search: { ...search, year: nextIsNextYear ? YEAR_FLAGS.NY : undefined, page: 1 } })
  }

  // Folding and unfolding both swap out actuals columns. A sort left pointing at one that is about
  // to disappear would keep ordering the rows with no header to show for it, so drop it either way.
  const handleExpandedChange = (nextIsExpanded: boolean) => {
    const nextColumnIds = actualsColumnIdsFor(nextIsExpanded)
    const isSortHidden =
      isActualsColumnId(search.sort?.id) &&
      !nextColumnIds.includes(search.sort?.id as ActualsColumnId)
    const sort = isSortHidden ? undefined : search.sort
    navigate({
      search: {
        ...search,
        isExpanded: nextIsExpanded || undefined,
        sort,
        page: 1,
      },
    })
  }

  // Key accounts are a view of their own rather than one of the categories: the general view's "All" and
  // its FMC / Vapour / NRT categories exclude the key-account (K/E) buckets.
  const isKeyAccountsView = search.budgetCategory === 'KEY_ACCOUNTS'

  const handleBudgetViewChange = (next: string) => {
    // A single-select toggle emits '' when the active item is clicked again; keep the current view.
    if (!next) return
    navigate({
      search: {
        ...search,
        budgetCategory: next === BUDGET_VIEWS.KEY_ACCOUNTS ? 'KEY_ACCOUNTS' : undefined,
        // The accounts view hides the estimate columns, so a sort on one of them would be invisible.
        sort:
          next === BUDGET_VIEWS.KEY_ACCOUNTS && ESTIMATE_COLUMN_IDS.has(search.sort?.id ?? '')
            ? undefined
            : search.sort,
        page: 1,
      },
    })
  }

  const handleBudgetCategoryChange = (next: string) => {
    navigate({
      search: {
        ...search,
        budgetCategory: next === INDETERMINATE ? undefined : (next as BudgetCategory),
        page: 1,
      },
    })
  }

  const handleShowResults = () => {
    navigate({ search: { ...search, keyword: keyword.trim() || undefined, page: 1 } })
  }

  const handleResetAllFilters = () => {
    setKeyword('')
    navigate({ search: { ...search, keyword: undefined, page: 1 } })
  }

  const onPageChange = (nextPage: number) => {
    navigate({ search: { ...search, page: nextPage + 1 } })
  }

  const onSortChange = (sort: ColumnSort) => {
    navigate({ search: { ...search, sort } })
  }

  const budgetCategoryValue = search.budgetCategory ?? INDETERMINATE

  const actualsColumns: ColumnDef<DisplayRow>[] = actualsColumnIdsFor(isExpanded).map((id) => ({
    id,
    accessorKey: id,
    header: ({ column }) => <TableHeader column={column} sortable align="right" />,
    cell: ({ row }) => <CompactCurrencyCell value={row.original[id]} />,
    meta: {
      displayTitle: t(`budgetTracker.table.${id}`),
      cellClassName: 'text-right text-muted-foreground',
      hideInColumnsMenu: true,
    },
  }))

  const allColumns: ColumnDef<DisplayRow>[] = [
    {
      id: 'budgetAllocation',
      accessorKey: 'budgetAllocation',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => <BudgetAllocation id={row.original.budgetAllocation} />,
      meta: { displayTitle: t('budgetTracker.table.budgetAllocation') },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.description ? <Text>{row.original.description}</Text> : <NoDataAvailable />,
      meta: { displayTitle: t('budgetTracker.table.description') },
    },
    {
      id: 'isActive',
      accessorKey: 'isActive',
      header: ({ column }) => <TableHeader column={column} />,
      cell: ({ row }) => <ActiveBadge isActive={row.original.isActive} />,
      enableSorting: false,
      meta: { displayTitle: t('budgetTracker.table.status'), hideOnFirstLoad: true },
    },
    {
      id: 'estimate',
      accessorKey: 'estimate',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: renderEstimateCell,
      meta: {
        // Estimates are typed in CAD whatever the display currency, so the column says so.
        displayTitle: t('currencyRates.cadColumn', { label: t('budgetTracker.table.estimate') }),
        cellClassName: 'text-right',
        hideInColumnsMenu: true,
      },
    },
    ...actualsColumns,
    {
      id: 'forecast',
      accessorKey: 'forecast',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.forecast} />,
      meta: {
        displayTitle: t('budgetTracker.table.forecast'),
        cellClassName: 'text-right text-muted-foreground',
        hideInColumnsMenu: true,
      },
    },
    {
      id: 'manuals',
      accessorKey: 'manuals',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.manuals} />,
      meta: {
        displayTitle: t('budgetTracker.table.manual'),
        cellClassName: 'text-right text-muted-foreground',
        hideInColumnsMenu: true,
      },
    },
    {
      id: 'budgetTotal',
      accessorKey: 'budgetTotal',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.budgetTotal} />,
      meta: {
        displayTitle: t('budgetTracker.table.total'),
        cellClassName: 'text-right',
        hideInColumnsMenu: true,
      },
    },
    {
      id: 'leftToSpend',
      accessorKey: 'leftToSpend',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => <CompactCurrencyCell isSigned value={row.original.leftToSpend} />,
      meta: {
        displayTitle: t('budgetTracker.table.leftToSpend'),
        cellClassName: 'text-right',
        hideInColumnsMenu: true,
      },
    },
    {
      id: 'monthlyLeftToSpend',
      accessorKey: 'monthlyLeftToSpend',
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) => <CompactCurrencyCell isSigned value={row.original.monthlyLeftToSpend} />,
      meta: {
        displayTitle: t('budgetTracker.table.monthlyLeftToSpend'),
        cellClassName: 'text-right',
        hideInColumnsMenu: true,
      },
    },
    {
      id: 'details',
      header: () => null,
      cell: ({ row }) => (
        <HStack className="justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t('budgetTracker.table.rowActions')}
              >
                <EllipsisVerticalIcon className="text-muted-foreground size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEditEstimates && !isKeyAccountsView && (
                <>
                  <DropdownMenuItem
                    onClick={() => setMonthsEditCode(row.original.budgetAllocation)}
                    className="cursor-pointer"
                  >
                    <PencilIcon className="size-4" />
                    {t('budgetTracker.leftToSpendMonths.action')}
                  </DropdownMenuItem>
                  <Separator />
                </>
              )}
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link
                  to="/crt/customer-investments/investments"
                  search={{
                    budgetAllocation: [row.original.budgetAllocation],
                    // Opens on the same version and manual-investment selection as the tracker.
                    crtPeriod: selectedCrtPeriodStr,
                    loadedVersionId: search.loadedVersionId,
                    manualInvestmentIds: search.manualInvestmentIds,
                  }}
                >
                  <ExternalLinkIcon className="size-4" />
                  {t('budgetTracker.table.viewInvestments')}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </HStack>
      ),
      enableSorting: false,
      meta: {
        headerClassName: 'w-16',
        cellClassName: 'w-16 text-right',
        hideInColumnsMenu: true,
      },
    },
  ]

  // Key-account budgets are tracked against contract estimates on the key-accounts tracker page, so the
  // accounts view shows spend only, without the bucket estimates and what is left of them.
  const columns = isKeyAccountsView
    ? allColumns.filter((column) => !ESTIMATE_COLUMN_IDS.has(column.id ?? ''))
    : allColumns

  return (
    <PageContainer maxWidth="wide" className={canEditEstimates ? 'pb-28' : undefined}>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.BUDGET_TRACKER}
        title={t('tutorials.dialog.budgetTracker.title')}
      >
        {t('tutorials.dialog.budgetTracker.description')}
      </IntroductionDialog>
      <Block shouldBlock={hasUnsavedChanges} />

      <Spread>
        <PageHeader>{t('budgetTracker.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={() =>
              queryClient.ensureQueryData(
                budgetTrackerExportQueryOptions({
                  crtPeriod: selectedCrtPeriodStr,
                  budgetCategory: search.budgetCategory,
                  year: search.year,
                  keyword: search.keyword,
                  manualInvestmentIds: search.manualInvestmentIds,
                }),
              )
            }
            rowCount={displayRows.length}
            label={t('export.viewData', { ns: 'common' })}
          />
          {canEditEstimates && !isKeyAccountsView && (
            <Button
              type="button"
              variant="secondary"
              disabled={isCopyPending}
              onClick={() => setIsCopyDialogOpen(true)}
            >
              <CopyIcon className="size-4" />
              {t('budgetTracker.copyPrevious.button')}
            </Button>
          )}
        </HStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            name={DATATABLE_NAMES.BUDGET_TRACKER}
            columns={columns}
            data={pageRows}
            rowCount={sortedRows.length}
            isPending={isPending}
            error={error}
            page={page}
            pageSize={pageSize}
            sort={search.sort}
            search={{
              className: 'w-64',
              value: keyword,
              setValue: setKeyword,
              placeholder: t('budgetTracker.searchPlaceholder'),
            }}
            queryParams={search as BudgetTrackerSearch}
            onShowResults={handleShowResults}
            onResetAllFilters={handleResetAllFilters}
            totals={totals}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSortChange={onSortChange}
            actions={
              <Spread align="center" className="w-full">
                <HStack gap="sm">
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    value={isKeyAccountsView ? BUDGET_VIEWS.KEY_ACCOUNTS : BUDGET_VIEWS.GENERAL}
                    onValueChange={handleBudgetViewChange}
                    aria-label={t('budgetTracker.filters.view')}
                    className="bg-background"
                  >
                    <ToggleGroupItem value={BUDGET_VIEWS.GENERAL}>
                      {t('budgetTracker.filters.view.general')}
                    </ToggleGroupItem>
                    <ToggleGroupItem value={BUDGET_VIEWS.KEY_ACCOUNTS}>
                      {t('budgetTracker.filters.view.accounts')}
                    </ToggleGroupItem>
                  </ToggleGroup>
                  {!isKeyAccountsView && (
                    <Select value={budgetCategoryValue} onValueChange={handleBudgetCategoryChange}>
                      <SelectTrigger className="bg-background w-auto gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            {t('budgetTracker.filters.budgetCategory')}
                          </span>
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={INDETERMINATE}>
                          {t('budgetTracker.filters.budgetCategory.viewAll')}
                        </SelectItem>
                        {GENERAL_BUDGET_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {t(`budgetTracker.filters.budgetCategory.${category}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </HStack>
                <YearSwitch isNextYear={isNextYear} onChange={handleYearChange} />
              </Spread>
            }
            toolbarActions={
              <>
                <ExpandDetailsButton isExpanded={isExpanded} onChange={handleExpandedChange} />
                <ConfigureAssumptions
                  isOpen={isConfigOpen}
                  onOpenChange={setIsConfigOpen}
                  crtPeriod={selectedCrtPeriodStr}
                  kinds={ASSUMPTION_KINDS}
                  selection={configuration}
                  onApply={handleApplyConfig}
                />
              </>
            }
            configBadges={configBadges}
            onConfigBadgeClick={() => setIsConfigOpen(true)}
          />
        </CardContent>
      </Card>

      {canEditEstimates && (
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isSavePending}
          onSaveChanges={handleSaveEstimates}
        />
      )}

      {monthsEditTarget && (
        <EditLeftToSpendMonthsDialog
          open
          onOpenChange={(next) => !next && setMonthsEditCode(null)}
          budgetAllocation={monthsEditTarget.budgetAllocation}
          description={monthsEditTarget.description}
          crtPeriod={selectedCrtPeriodStr}
          savedMonths={monthsEditTarget.leftToSpendMonths}
          windowMonths={data?.leftToSpendWindow ?? []}
        />
      )}

      <AlertDialog open={isCopyDialogOpen} onOpenChange={setIsCopyDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('budgetTracker.copyPrevious.dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {t('budgetTracker.copyPrevious.dialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel', { ns: 'common' })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCopyPrevious}>
              {t('budgetTracker.copyPrevious.dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
